import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import axios from 'axios'

/**
 * Câștigătorii campaniei, la o adresă proprie: `/s/<cod>/castigatori`.
 *
 * Publicarea nu e o alegere de comunicare: OG 99/2000 art. 42 alin. (2) o cere, iar regulamentul
 * SCAN & WIN o prevede la art. 8.5 — unde adresa lipsea, pentru că pagina nu exista. Modelul
 * arătat de Alexandru (campania Nestlé de la Mega Image) are exact aceeași secțiune, cu
 * placeholder până la extragere.
 *
 * Pagina răspunde și înainte de extragere, cu lista goală. Adresa trebuie să existe și să
 * funcționeze din prima zi a campaniei, fiindcă e scrisă în regulament — o adresă care dă eroare
 * până la extragere e mai rea decât una goală.
 *
 * Se afișează puțin: numărul extras și prenumele cu inițiala numelui. Atât cere legea, iar
 * regulamentul insistă pe „limitarea datelor afișate la ceea ce este necesar".
 */

interface Castigator {
  id: number
  nrExtras: number
  nume: string
  extrasLa: string
  /** Cum s-a stabilit numărul: random.org, sau „un singur înscris" când n-a fost ce extrage. */
  sursa: string
  areDovada: boolean
  /** Înregistrarea extragerii la random.org, când a trecut prin Draw Service. */
  recordUrl: string | null
  /** Premiul extras dar neridicat în termen: s-a reportat, potrivit art. 6.3 și 4.8. */
  neridicat: boolean
}

/**
 * Duce dovada la formularul public de verificare al celor de la random.org.
 *
 * Alexandru: „păi atunci cum poate un câștigător să verifice?". Aici, pe pagina la care trimite
 * regulamentul. Răspunsul semnat pleacă prin POST către ei, iar ei confirmă că numărul a ieșit
 * acolo, la momentul acela, pentru intervalul acela. Semnătura e a lor, nu a noastră — deci nu o
 * putem falsifica, și tocmai asta o face dovadă.
 *
 * Prin formular, nu prin link: verificarea lor primește datele prin POST, iar semnătura e prea
 * lungă pentru un URL.
 */
async function verificaLaRandomOrg(slug: string, id: number) {
  const d = (
    await axios.get<{ random: unknown; signature: string }>(
      `/api/public/sampling/${encodeURIComponent(slug)}/extrageri/${id}/dovada`,
    )
  ).data
  const f = document.createElement('form')
  f.method = 'POST'
  f.action = 'https://api.random.org/signatures/form'
  f.target = '_blank'
  f.rel = 'noreferrer'
  for (const [nume, valoare] of [
    ['format', 'json'],
    ['random', JSON.stringify(d.random)],
    ['signature', d.signature],
  ]) {
    const i = document.createElement('input')
    i.type = 'hidden'
    i.name = nume
    i.value = valoare
    f.appendChild(i)
  }
  document.body.appendChild(f)
  f.submit()
  f.remove()
}

interface Date_ {
  denumire: string
  castigatori: Castigator[]
  /** Regulamentul pe site-ul firmei, când acțiunea are link extern. */
  urlRegulament: string | null
}

export function CastigatoriPage() {
  const { slug = '' } = useParams()
  const [date, setDate] = useState<Date_ | null>(null)
  const [eroare, setEroare] = useState<string | null>(null)

  useEffect(() => {
    let anulat = false
    axios
      .get<Date_>(`/api/public/sampling/${encodeURIComponent(slug)}/castigatori`)
      .then((r) => {
        if (!anulat) setDate(r.data)
      })
      .catch(() => {
        if (!anulat) setEroare('Nu am găsit această campanie.')
      })
    return () => {
      anulat = true
    }
  }, [slug])

  return (
    <div className="min-h-screen bg-b4l-bg px-4 py-8">
      <div className="mx-auto max-w-2xl">
        {eroare && <p className="text-sm text-b4l-red">{eroare}</p>}
        {!date && !eroare && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}

        {date && (
          <>
            <h1 className="text-2xl font-semibold text-b4l-text">Câștigători</h1>
            <p className="text-sm text-b4l-text-muted">{date.denumire}</p>

            {date.castigatori.length === 0 ? (
              /*
                Înainte de extragere. Formularea contează: „nu s-a făcut încă" liniștește, „nu
                există câștigători" sperie pe cine a participat și verifică.
              */
              <div className="mt-6 rounded-lg bg-white p-4">
                <p className="text-sm text-b4l-text">
                  Extragerea nu a avut loc încă. Câștigătorii vor fi publicați aici, conform
                  regulamentului campaniei.
                </p>
              </div>
            ) : (
              <div className="mt-6 overflow-hidden rounded-lg bg-white">
                {date.castigatori.map((c) => (
                  <div
                    key={`${c.nrExtras}-${c.extrasLa}`}
                    className="flex items-center gap-4 border-b border-b4l-border px-4 py-3 last:border-0"
                  >
                    <span className="w-12 shrink-0 text-lg font-semibold tabular-nums text-b4l-text">
                      {c.nrExtras}
                    </span>
                    <span className="flex-1 text-sm font-medium text-b4l-text">{c.nume}</span>
                    <span className="text-xs text-b4l-text-muted">
                      {new Date(c.extrasLa).toLocaleDateString('ro-RO', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </span>
                    {/*
                      Verificarea, la îndemâna oricui. Când baza a avut un singur înscris nu s-a
                      extras nimic — se spune, în loc să se ofere un link care n-ar duce nicăieri.
                    */}
                    {c.neridicat && (
                      <span
                        className="shrink-0 rounded-full bg-b4l-amber-light px-2 py-0.5 text-xs text-b4l-amber-dark"
                        title="Premiul nu a fost ridicat în termenul din regulament și a fost reportat"
                      >
                        premiu reportat
                      </span>
                    )}
                    {c.recordUrl ? (
                      <a
                        href={c.recordUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 text-xs text-b4l-blue underline"
                        title="Înregistrarea extragerii la random.org. Verifici cu numărul înscrierii tale."
                      >
                        verifică la random.org ↗
                      </a>
                    ) : c.areDovada ? (
                      <button
                        onClick={() => void verificaLaRandomOrg(slug, c.id).catch(() => {})}
                        className="shrink-0 text-xs text-b4l-blue underline"
                        title="Deschide verificarea la random.org, cu semnătura acestei extrageri"
                      >
                        verifică ↗
                      </button>
                    ) : (
                      <span className="shrink-0 text-xs text-b4l-text-dim">{c.sursa}</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/*
              Explicatia pentru „premiu reportat". Victor: „trebuie sa punem ceva in lista aceasta sa
              se inteleaga ca premiul s-a raportat (...) pt ca oamenii se uita acolo".

              Fara o fraza care sa spuna ce inseamna eticheta, un numar castigator langa care scrie
              „reportat" ridica mai multe intrebari decat lamureste.
            */}
            {date.castigatori.some((c) => c.neridicat) && (
              <p className="mt-4 rounded-lg bg-b4l-amber-light p-3 text-xs text-b4l-amber-dark">
                <strong>Premiu reportat</strong> înseamnă că numărul a fost extras corect, dar
                câștigătorul nu a putut fi validat sau nu s-a prezentat să ridice premiul în
                termenul din regulament. Premiul nu se pierde: se acordă printr-o extragere nouă,
                dintr-o bază formată din înscrierile de după (art. 4.7 și 6.3).
              </p>
            )}
            <p className="mt-4 text-xs text-b4l-text-muted">
              Premiile acordate sunt cele descrise în{' '}
              {date.urlRegulament ? (
                <a
                  href={date.urlRegulament}
                  target="_blank"
                  rel="noreferrer"
                  className="text-b4l-blue underline"
                >
                  regulamentul campaniei
                </a>
              ) : (
                <Link
                  to={`/s/${encodeURIComponent(slug)}/reguli#regulament`}
                  className="text-b4l-blue underline"
                >
                  regulamentul campaniei
                </Link>
              )}
              . Se afișează numărul de ordine, prenumele și inițiala numelui — atât cât este necesar
              pentru transparența extragerii.
            </p>
            {/*
              Explicatia de sub lista. Un buton „verifică" fara nicio lamurire nu e transparenta —
              nimeni nu apasa pe ceva ce nu stie ce face.
            */}
            {/*
              Instructiunile de verificare, cerute de Alexandru: „putem pune niste scurte
              instructiuni acolo unde participantul isi poate face verificarea?".

              Pagina de la random.org cere un „entrant identifier" si nu spune de unde sa-l ia — la
              noi acela e NUMARUL INSCRIERII, cel din emailul de confirmare. Fara randul asta, omul
              ajunge pe o pagina in engleza, cu un camp gol, si renunta; sau scrie „14" si primeste
              „not found", ceea ce e mai rau decat sa nu fi incercat.
            */}
            <div className="mt-4 rounded-lg bg-white p-3">
              <p className="text-sm font-medium text-b4l-text">Cum verifici singur extragerea</p>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-b4l-text-muted">
                <li>
                  Apasă <strong>verifică la random.org</strong>, lângă câștigătorul care te
                  interesează.
                </li>
                <li>Pe pagina lor, bifează caseta de confirmare că nu ești robot.</li>
                <li>
                  La <em>Entrant identifier</em> scrie <strong>numărul înscrierii tale</strong> — îl
                  ai în e-mailul de confirmare, în forma <code>TB-2026-000014</code>. Scrie-l întreg
                  și cu majuscule; doar cifra nu funcționează.
                </li>
                <li>
                  Apasă <strong>Go!</strong> — ei îți spun dacă ai fost în extragere și dacă ai fost
                  ales câștigător.
                </li>
              </ol>
              <p className="mt-2 text-xs text-b4l-text-muted">
                Răspunsul vine de la random.org, nu de la noi. Extragerea e ținută de ei ca terț
                independent, iar înregistrarea rămâne la ei ani de zile. Nu le transmitem nume,
                telefoane sau adrese de e-mail — doar numerele de înscriere.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
